import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Container, Box, Typography, TextField, Button, CircularProgress, Alert, List, ListItem, ListItemText, Divider, Paper,CssBaseline } from '@mui/material'; // Import Material UI components

const HomePage = () => {
  const [allSolutions, setAllSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [inputQuery, setInputQuery] = useState(''); // 输入框状态
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [totalPages, setTotalPages] = useState(1);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [searching, setSearching] = useState(false); // 搜索状态
  
// 🔹 翻译文本（仅翻译包含英文的部分，保留空格）
const translateText = async (text) => {
  const containsEnglish = /[a-zA-Z]/.test(text);
  if (!containsEnglish) return text; // 如果没有英文，则直接返回原文本

  try {
    // 将输入文本拆分成单词和非单词部分（比如标点符号）
    const words = text.split(/(\s+)/); // 使用正则拆分成单词和空格

    // 遍历拆分后的单词和空格
    const translatedWords = await Promise.all(
      words.map(async (word) => {
        if (word.trim()) {
          // 只有英文单词才进行翻译
          try {
            const response = await axios.get('https://api.mymemory.translated.net/get', {
              params: { q: word, langpair: 'en|zh-CN' },
            });
            return response.data.responseData.translatedText;
          } catch (error) {
            console.error('翻译失败:', error);
            return word; // 如果翻译失败，返回原始单词
          }
        } else {
          return word; // 非单词部分（空格）保持不变
        }
      })
    );

    // 返回拼接后的翻译结果，保留空格
    return translatedWords.join('');
  } catch (error) {
    console.error('翻译失败:', error);
    return text; // 如果外部请求失败，则返回原文本
  }
};


  // 🔹 过滤搜索结果
  const filteredSolutions = useMemo(() => {
    if (!searchQuery.trim()) {
      return allSolutions;
    }
    const keywords = searchQuery.trim().toLowerCase().split(/\s+/);
    return allSolutions.filter((solution) => {
      const searchableText = `${solution.projectName} ${solution.projectRequirements} ${solution.projectRequirementsDetails}`.toLowerCase();
      return keywords.every((keyword) => searchableText.includes(keyword));
    });
  }, [allSolutions, searchQuery]);

  const solutionsToDisplay = useMemo(() => {
  //  return isPaidUser ? filteredSolutions : filteredSolutions.slice(0, 5);
  //可根据 isPaidUser 控制展示的数量，目前直接返回全部
    return filteredSolutions;
  }, [filteredSolutions, isPaidUser]);

  useEffect(() => {
    const fetchSolutions = async () => {
      setLoading(true);
      setError('');
//      console.log('搜索关键词:', searchQuery);
      try {
        const response = await axios.get('/solutions/search', {
          params: {
            query: searchQuery,
            categories: selectedCategories,
            tags: selectedTags,
            page: currentPage,
            pageSize: pageSize,
          },
        });
//        console.log('获取解决方案列表成功:', response.data);
        setAllSolutions(response.data || []);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
        setLoading(false);
      } catch (error) {
        setError('获取解决方案列表失败');
//        setLoading(false);
      }
      finally {
        setLoading(false);
        setSearching(false); // 在请求结束后，再结束搜索状态
      }
    };

    if (searchQuery) {
      fetchSolutions();
    }
  }, [searchQuery, selectedCategories, selectedTags, currentPage, pageSize]);
  // 处理回车搜索
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  // 🔹 处理搜索按钮点击
  const handleSearch = async () => {
    setSearching(true);
    const translatedQuery = await translateText(inputQuery); // 先翻译
//    console.log('搜索--翻译结果:', translatedQuery);
    setSearchQuery(translatedQuery); // 更新搜索关键词
    setCurrentPage(1); // 重置页码
    //setSearching(false);
  };

  const location = useLocation(); // 在页面组件中使用 useLocation
  const navigate = useNavigate(); // 初始化 useNavigate

  useEffect(() => {
    document.title = '首页 - 责联网--产品大师智库'; // 设置当前页面的标题
  }, [location.pathname]); // 依赖 location.pathname，当路径改变时更新标题

  const handleCreateNewRequirement = () => {
    navigate('/requirements', { state: { searchKeywords: inputQuery } }); // Pass inputQuery as state
  };

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 4 }}> {/* Main container with maxWidth 'md' and marginTop */}
    <CssBaseline /> {/* Optional: CssBaseline for consistent styling */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}> {/* Centered content container, marginBottom */}
        <Typography component="h1" variant="h4" align="center" gutterBottom> {/* Main title */}
            人人都是产品大师，激发需求、放飞欲望
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary"> {/* Subtitle */}
            解决方案列表
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" mb={2}> {/* Preview text for free users */}
            {!isPaidUser ? '免费用户预览 - 前 5 条' : '付费用户 - 完整列表'}
        </Typography>
    </Box>

    {/* Search Bar */}
    <Box sx={{ display: 'flex', mb: 3, width: '100%', maxWidth: 800 }}> {/* Search bar container, Flex layout, maxWidth */}
        <TextField
            fullWidth
            id="searchQuery"
            label="搜索关键词"
            variant="outlined"
            placeholder="请输入关键词搜索解决方案"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyDown={handleKeyDown}
        /> {/* Search TextField */}
        <Button 
            variant="contained" 
            onClick={handleSearch} 
            sx={{ ml: 2 }}
        > {/* Search Button */}
            搜索
        </Button>
    </Box>

    {/* Search Status Message */}
    <Box mb={2}>
        {searching ? (
            <CircularProgress size={24} /> // Loading indicator when searching
        ) : solutionsToDisplay.length === 0 ? (
            <Alert severity="warning">没匹配到，请换关键词查询</Alert> // Alert for no results
        ) : (
            <Typography variant="subtitle2" color="success" fontWeight="bold">
                共找到：{solutionsToDisplay.length}条解决方案
            </Typography> // Success message with solution count
        )}
        {error && ( // Error Alert if fetching solutions failed
            <Alert severity="error">{error}</Alert>
        )}
    </Box>

    {/* Solution List */}
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}> {/* Solution List container */}
        {solutionsToDisplay.map((solution, index) => (
            <React.Fragment key={solution._id}> {/* React.Fragment to avoid extra div */}
                <ListItem alignItems="flex-start"> {/* List Item for each solution */}
                    <ListItemText
                        primary={
                            <Typography variant="h6" component={Link} to={`/solutions/${solution._id}/view`} target="_blank" rel="noopener noreferrer" color="primary" underline="hover">
                                {solution.projectName}
                            </Typography> // Solution Project Name as primary text, linked
                        }
                        secondary={
                            <React.Fragment>
                                <Typography
                                    sx={{ display: 'inline' }}
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                >
                                    项目需求:
                                </Typography>
                                {` — ${solution.projectRequirements.substring(0, 150)}...`}
                                <Typography
                                    sx={{ display: 'block' }} // Display details on a new line
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    详细要求: {solution.projectRequirementsDetails?.substring(0, 100)}...
                                </Typography>
                            </React.Fragment> // Solution details as secondary text
                        }
                    />
                </ListItem>
                {index !== solutionsToDisplay.length - 1 && <Divider component="li" />} {/* Divider after each item except the last */}
            </React.Fragment>
        ))}
    </List>
    {/* Conditionally render "Create New Requirement" button */}
    {!searching && !loading && !error && solutionsToDisplay.length < 3 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="contained" color="primary" onClick={handleCreateNewRequirement}>
                        创建新需求
                    </Button>
                </Box>
    )}
</Container>
);
};

export default HomePage;
